/** @typedef {import("../../api/types").Order} Order */
/** @typedef {import("../../api/types").OrderNeighbors} OrderNeighbors */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {useLocation, useParams} from "react-router-dom";
import {Alert, Button, Card, CardBody, CardHeader, Container, ListGroup, ListGroupItem, Spinner} from "reactstrap";
import {DeviceSummary, DiscountSummary, ItemSummary, TotalsCard} from "./components/OrderSummaries";
import OrderActions from "./components/OrderActions";
import OrderMenu from "./components/OrderMenu";
import {useOrderAction} from "./components/useOrderAction";
import OrderHistoryModal from "./components/OrderHistoryModal";
import ShareMenu from "./components/ShareMenu";
import ShareNotice from "./components/ShareNotice";
import {useShare} from "./components/useShare";
import RefreshPricesButton from "./components/RefreshPricesButton";
import OrderNavigation from "./components/OrderNavigation";
import {listFilterForNeighbors} from "./utils/orderNeighbors";
import {orderCost, orderMargin} from "./utils/orderTotals";
import {markOrderNewsRead} from "../../shared/utils/notifications";
import AddressLink from "../../shared/components/AddressLink";
import EmailLink from "../../shared/components/EmailLink";
import PhoneLink from "../../shared/components/PhoneLink";
import OrderStatusBadge from "./components/OrderStatusBadge";
import {formatDate} from "../../shared/utils/format";
import ListLink from "../../shared/components/ListLink";
import IconLabel from "../../shared/components/IconLabel";
import {
    BoxIcon,
    DeviceIcon,
    HistoryIcon,
    ListIcon,
    NoteIcon,
    PercentIcon,
    UserIcon,
} from "../../shared/components/icons";

const ORDERS_URL = "/os/api/service_orders/";

const OrderDetail = () => {
    const {id} = useParams();
    // Opened from the inbox: "back" leads there instead of to the orders.
    const fromInbox = useLocation().state?.backTo === "/notifications";
    const [order, setOrder] = useState(/** @type {Order|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [neighbors, setNeighbors] = useState(/** @type {OrderNeighbors|null} */ (null));

    // Moving to another order starts it from the top, like a page of its own.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    // The orders before and after this one, by number, inside what the list was showing.
    useEffect(() => {
        const controller = new AbortController();
        setNeighbors(null);

        axios
            .get(`${ORDERS_URL}${encodeURIComponent(id)}/neighbors/`, {
                params: listFilterForNeighbors(),
                // ?status=PA&status=CA (not status[]=PA), as the API expects
                paramsSerializer: {indexes: null},
                signal: controller.signal,
            })
            .then(({data}) => setNeighbors({previous: data.previous ?? null, next: data.next ?? null}))
            .catch(() => {
                // The buttons stay disabled; the order itself is unaffected.
            });

        return () => controller.abort();
    }, [id]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setOrder(null);

        axios
            .get(`${ORDERS_URL}${encodeURIComponent(id)}/`, {signal: controller.signal})
            .then(({data}) => {
                setOrder(data);
                // seeing the order is seeing the news about it
                markOrderNewsRead(data.id);
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError(
                    err.response?.status === 404
                        ? "Ordem de serviço não encontrada."
                        : "Não foi possível carregar a ordem de serviço."
                );
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [id]);

    return (
        <Container className="mt-4 mb-5" style={{maxWidth: 860}}>
            <div className="d-flex justify-content-between align-items-center gap-2">
                {fromInbox ? (
                    <ListLink basePath="/notifications">&larr; Avisos</ListLink>
                ) : (
                    <ListLink basePath="/orders">&larr; Ordens de serviço</ListLink>
                )}
                <OrderNavigation neighbors={neighbors} />
            </div>
            {loading && (
                <div className="text-center mt-4">
                    <Spinner />
                </div>
            )}
            {error && (
                <Alert color="danger" className="mt-3">
                    {error}
                </Alert>
            )}
            {order && <OrderBody order={order} onUpdated={setOrder} />}
        </Container>
    );
};

// fields_content is a JSON-encoded object (string) per the API schema.
const parseFields = (content) => {
    try {
        const parsed = JSON.parse(content || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

/** Same sections, in the same order, as the order form (pages/orderNew.jsx). */
/** @param {{order: Order, onUpdated: (order: Order) => void}} props */
const OrderBody = ({order, onUpdated}) => {
    const {currency, client, order_items: items, order_devices: devices} = order;
    const products = order.order_products ?? [];
    const discountAmount = Number(order.formatted_absolute_discount) || 0;
    const total = Number(order.total_value);
    const cost = orderCost([...items, ...products]);
    const action = useOrderAction(order, onUpdated);
    const share = useShare(order, onUpdated);
    const [historyOpen, setHistoryOpen] = useState(false);

    return (
        <>
            <div className="mt-3 mb-3 d-flex flex-wrap align-items-start justify-content-between gap-2 gap-md-3">
                <div>
                    <div className="d-flex align-items-center">
                        <h3 className="order-title mb-0 me-3">Ordem de serviço nº {order.number}</h3>
                        <OrderStatusBadge status={order.status} />
                    </div>
                    <Button
                        color="link"
                        className="p-0 mt-1 small text-muted text-decoration-none text-start"
                        title="Ver o histórico da ordem"
                        onClick={() => setHistoryOpen(true)}
                    >
                        <span className="d-inline-flex align-items-center">
                            {/* the icon is for roomy screens; on a phone the two date lines stand alone */}
                            <span className="d-none d-sm-inline-flex me-2">
                                <HistoryIcon size={14} />
                            </span>
                            <span>
                                <span className="d-block d-sm-inline">Criada em {formatDate(order.created_at)}</span>
                                {order.expiration && (
                                    <>
                                        <span className="d-none d-sm-inline"> · </span>
                                        <span className="d-block d-sm-inline">
                                            Validade: {formatDate(order.expiration)}
                                        </span>
                                    </>
                                )}
                            </span>
                        </span>
                    </Button>
                </div>
                <div className="d-flex align-items-center gap-2 ms-auto order-tools">
                    <ShareMenu order={order} share={share} />
                    <OrderMenu order={order} action={action} />
                </div>
            </div>

            <ShareNotice share={share} />
            {historyOpen && (
                <OrderHistoryModal
                    orderId={order.id}
                    number={order.number}
                    currency={order.currency}
                    onClose={() => setHistoryOpen(false)}
                />
            )}

            <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                    <Card className="h-100">
                        <CardHeader>
                            <IconLabel icon={<UserIcon />}>Cliente</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <div className="fw-semibold">{client?.name}</div>
                            {client?.address && (
                                <div>
                                    <AddressLink address={client.address} />
                                </div>
                            )}
                            {client?.email && (
                                <div>
                                    <EmailLink email={client.email} />
                                </div>
                            )}
                            {[client?.phone, client?.alternate_phone].filter(Boolean).map((p) => (
                                <div key={p}>
                                    <PhoneLink phone={p} />
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </div>
                <div className="col-12 col-md-6">
                    <Card className="h-100">
                        <CardHeader>
                            <IconLabel icon={<NoteIcon />}>Observações</IconLabel>
                        </CardHeader>
                        <CardBody>
                            {order.observations ? (
                                <div style={{whiteSpace: "pre-wrap"}}>{order.observations}</div>
                            ) : (
                                <span className="text-muted">Nenhuma observação.</span>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>

            <Card className="mb-3">
                <CardHeader>
                    <IconLabel icon={<DeviceIcon />}>Equipamentos do cliente</IconLabel>
                </CardHeader>
                <CardBody>
                    {devices?.length ? (
                        <ListGroup>
                            {devices.map((device, index) => (
                                <ListGroupItem className="d-flex align-items-start gap-2" key={index}>
                                    <DeviceSummary
                                        typeName={device.device_type?.name}
                                        description={device.description}
                                        fields={parseFields(device.fields_content)}
                                    />
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    ) : (
                        <span className="text-muted">Nenhum equipamento.</span>
                    )}
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardHeader className="d-flex align-items-center justify-content-between">
                    <IconLabel icon={<ListIcon />}>Serviços</IconLabel>
                    {order.available_actions?.includes("refresh_prices") && (
                        <span>
                            <RefreshPricesButton order={order} lines="services" onUpdated={onUpdated} />
                        </span>
                    )}
                </CardHeader>
                <CardBody>
                    {items?.length ? (
                        <ListGroup>
                            {items.map((item) => (
                                <ListGroupItem className="d-flex align-items-start gap-2" key={item.id}>
                                    <ItemSummary
                                        name={item.service_item?.name}
                                        quantity={item.quantity}
                                        unit={Number(item.base_value)}
                                        cost={item.cost === undefined ? undefined : Number(item.cost)}
                                        total={Number(item.value)}
                                        currency={currency}
                                    />
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    ) : (
                        <span className="text-muted">Nenhum serviço.</span>
                    )}
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardHeader className="d-flex align-items-center justify-content-between">
                    <IconLabel icon={<BoxIcon />}>Produtos</IconLabel>
                    {order.available_actions?.includes("refresh_prices") && (
                        <span>
                            <RefreshPricesButton order={order} lines="products" onUpdated={onUpdated} />
                        </span>
                    )}
                </CardHeader>
                <CardBody>
                    {products.length ? (
                        <ListGroup>
                            {products.map((line) => (
                                <ListGroupItem className="d-flex align-items-start gap-2" key={line.id}>
                                    <ItemSummary
                                        name={line.product?.name}
                                        quantity={line.quantity}
                                        unit={Number(line.base_value)}
                                        cost={line.cost === undefined ? undefined : Number(line.cost)}
                                        total={Number(line.value)}
                                        currency={currency}
                                    />
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    ) : (
                        <span className="text-muted">Nenhum produto.</span>
                    )}
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardHeader>
                    <IconLabel icon={<PercentIcon />}>Desconto</IconLabel>
                </CardHeader>
                <CardBody>
                    {order.discount_type && Number(order.discount_value) > 0 ? (
                        <ListGroup>
                            <ListGroupItem className="d-flex align-items-start gap-2">
                                <DiscountSummary
                                    type={order.discount_type}
                                    value={Number(order.discount_value)}
                                    description={order.discount_description ?? undefined}
                                    amount={discountAmount}
                                    currency={currency}
                                />
                            </ListGroupItem>
                        </ListGroup>
                    ) : (
                        <span className="text-muted">Nenhum desconto aplicado.</span>
                    )}
                </CardBody>
            </Card>

            <TotalsCard
                subtotal={total + discountAmount}
                discount={discountAmount}
                total={total}
                currency={currency}
                totalLabel="Total"
                cost={cost}
                margin={orderMargin(total, cost)}
            />

            <OrderActions order={order} action={action} />
        </>
    );
};

export default OrderDetail;
